import React from "react";
import styled from "@emotion/styled";
import { Button } from "./Button";
import { ScreenReaderText } from "./ScreenReaderText";

type TabContent = {
  title: string;
  content: React.ReactNode;
};
type TabsProps = {
  tabsContent: TabContent[];
};

function Tabs({ tabsContent }: TabsProps) {
  const [currentTab, setCurrentTab] = React.useState(0);

  return (
    <div>
      <TabRow role="tablist">
        {tabsContent.map((tab, index) => {
          const tabId = stringToSlug(tab.title);
          return (
            <TabLi key={tab.title}>
              <TabButton
                onClick={() => setCurrentTab(index)}
                isCurrent={currentTab === index}
                role="tab"
                aria-selected={currentTab === index}
                aria-expanded={currentTab === index}
                aria-controls={tabId}
                id={tabId}
              >
                {tab.title}
              </TabButton>
            </TabLi>
          );
        })}
      </TabRow>
      {/* map list of tabs, as a section containing the title and content */}
      {tabsContent.map((tab, index) => (
        <TabPanel
          key={tab.title}
          title={tab.title}
          content={tab.content}
          aria-hidden={currentTab !== index}
        />
      ))}
    </div>
  );
}

function stringToSlug(text: string) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const TabRow = styled.div(({ theme }) => ({
  display: "flex",
  gap: "1rem",
  borderBottom: "2px solid",
  borderColor: theme.colors.fg20,
}));

const TabLi = styled.li({
  position: "relative",
  transform: "translateY(3px)",
  display: "inline-block",
  border: 0,
  padding: 0,
  button: {
    position: "static",
    "&, &:hover, &:focus": { border: 0 },
    paddingTop: ".66em",
    paddingBottom: ".33em",
  },
});

const TabButton = styled(Button)<{ isCurrent: boolean }>(
  ({ theme, isCurrent }) => ({
    color: theme.colors.fg70,
    fontSize: theme.fontSizes.xs,
    [theme.breakpoints.desktop]: {
      fontSize: theme.fontSizes.sm,
    },
    "&::after": {
      content: '""',
      display: "block",
      position: "absolute",
      left: -2,
      right: -2,
      bottom: 0,
      height: 0,
      borderRadius: theme.borderRadius.round,
    },
    "&:hover, &:focus-visible": {
      color: theme.colors.fg,
    },

    "&:hover::after, &:focus-visible::after": {
      backgroundColor: theme.colors.fg30,
      height: 4,
    },
    ...(isCurrent && {
      color: theme.colors.fg,
      "&::after,:hover::after, &:focus-visible::after": {
        backgroundColor: theme.colors.fg,
        height: 4,
      },
    }),
  })
);
TabButton.defaultProps = {
  size: "xs",
  variant: "blank",
};

type TabPanelProps = {
  title: string;
  content: React.ReactNode;
};
function TabPanel({ title, content, ...props }: TabPanelProps) {
  if (!title || !content) return null;

  return (
    <TabPanelElement role="tabpanel" id={stringToSlug(title)} {...props}>
      <ScreenReaderText as="h3">{title}</ScreenReaderText>
      {content}
    </TabPanelElement>
  );
}

const TabPanelElement = styled.section({
  padding: "1.5rem 0",
  '&[aria-hidden="true"]': {
    display: "none",
  },
});

export { Tabs };
